import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  Timestamp,
} from "firebase/firestore";
import { db } from "../../../firebase";
import {
  FaTimes,
  FaChevronDown,
  FaBoxOpen,
  FaCircleNotch,
  FaWhatsapp,
  FaMapMarkerAlt,
  FaExpandArrowsAlt,
  FaEdit,
  FaSave,
  FaHistory,
} from "react-icons/fa";
import "./ViewRetailerOrder.scss";

const normalizeStatus = (s) =>
  s
    ? String(s).trim().charAt(0).toUpperCase() +
      String(s).trim().slice(1).toLowerCase()
    : "Pending";

const extractImages = (order) => {
  let imgs = [];
  if (order?.files?.length > 0) imgs.push(...order.files);
  if (order?.singleItems?.length > 0) {
    order.singleItems.forEach((item) => {
      if (item.fileUrls) imgs.push(...item.fileUrls);
    });
  }
  return imgs;
};

const getProgress = (status) => {
  if (status === "Pending") return 1;
  if (status === "Processing") return 2;
  if (["Polishing", "Setting", "Hallmarked"].includes(status)) return 3;
  if (["Complete", "Delivered"].includes(status)) return 4;
  return 1;
};

const ViewRetailerOrder = ({ id }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isClosing, setIsClosing] = useState(false);

  // UX States
  const [lightboxImg, setLightboxImg] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({});

  const session = useMemo(() => {
    const stored = localStorage.getItem("retailerSession");
    return stored ? JSON.parse(stored) : { partyName: "Retailer" };
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const docRef = doc(db, "partyOrders", id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = { id: docSnap.id, ...docSnap.data() };
          setOrder(data);

          const details =
            data.orderType === "Single"
              ? data.singleItems?.[0]
              : data.castingDetails;
          setEditForm({
            weight: details?.weight || "",
            length: details?.length || details?.size || "",
            quantity: data.totalQuantity || details?.quantity || 1,
            comments: data.comments || "",
            carat: details?.carat || "",
            stone: details?.stone || "",
          });
        }
      } catch (error) {
        console.error("Error fetching order:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      params.delete("viewOrder");
      setSearchParams(params);
    }, 300);
  };

  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const docRef = doc(db, "partyOrders", order.id);
      let updates = {
        comments: editForm.comments,
        totalQuantity: parseInt(editForm.quantity) || 1,
        adminUnread: true,
        adminAlertMessage: "Retailer edited order details.",
        editHistory: arrayUnion({
          editedBy: session.partyName,
          timestamp: Timestamp.now(),
          action: "Modified Specs/Instructions",
        }),
      };

      if (order.orderType === "Single") {
        let updatedItems = [...order.singleItems];
        updatedItems[0] = {
          ...updatedItems[0],
          weight: editForm.weight,
          length: editForm.length,
          quantity: parseInt(editForm.quantity) || 1,
        };
        updates.singleItems = updatedItems;
      } else if (order.orderType === "Casting") {
        updates.castingDetails = {
          ...order.castingDetails,
          weight: editForm.weight,
          size: editForm.length,
          carat: editForm.carat,
          stone: editForm.stone,
        };
      }

      await updateDoc(docRef, updates);

      setOrder((prev) => ({
        ...prev,
        ...updates,
        editHistory: [
          ...(prev.editHistory || []),
          {
            editedBy: session.partyName,
            timestamp: { seconds: Date.now() / 1000 },
            action: "Modified Specs/Instructions",
          },
        ],
      }));

      setIsEditing(false);
    } catch (error) {
      alert("Failed to save changes.");
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="vr-view-overlay">
        <div className="vr-view-loader">
          <FaCircleNotch className="vr-view-spinner" /> Loading Order...
        </div>
      </div>
    );
  }

  if (!order) return null;

  const images = extractImages(order);
  const status = normalizeStatus(order.status);
  const progressStep = getProgress(status);
  const orderDetails =
    order.orderType === "Single"
      ? order.singleItems?.[0]
      : order.castingDetails;
  const canEdit = status === "Pending";

  return (
    <div className={`vr-view-overlay ${isClosing ? "vr-view-closing" : ""}`}>
      <div className="vr-view-backdrop" onClick={handleClose}></div>

      {lightboxImg && (
        <div
          className="vr-view-lightbox vr-view-fade-in"
          onClick={() => setLightboxImg(null)}
        >
          <button className="vr-view-lightbox-close">
            <FaTimes />
          </button>
          <img src={lightboxImg} alt="Fullscreen Reference" />
        </div>
      )}

      <div className={`vr-view-wrapper ${isClosing ? "vr-view-closing" : ""}`}>
        <header className="vr-view-header">
          <div className="vr-view-mobile-pull-bar"></div>
          <div className="vr-view-header-content">
            <div className="vr-view-header-titles">
              <span className="vr-view-id-badge">Order #{order.orderId}</span>
              <h2 className="vr-view-product-title">
                {order.product || order.orderType}
              </h2>
            </div>
            <button className="vr-view-close-btn" onClick={handleClose}>
              <FaTimes className="vr-view-desktop-icon" />
              <FaChevronDown className="vr-view-mobile-icon" />
            </button>
          </div>
        </header>

        <div className="vr-view-scroll-body vr-view-fade-in-up">
          {/* TIMELINE */}
          <section className="vr-view-section">
            <div className="vr-view-timeline">
              <div className="vr-view-timeline-track">
                <div
                  className="vr-view-timeline-fill"
                  style={{ width: `${(progressStep - 1) * 33.33}%` }}
                ></div>
              </div>
              <div className="vr-view-timeline-steps">
                <div
                  className={`vr-view-step ${progressStep >= 1 ? "vr-view-active" : ""}`}
                >
                  <span>Pending</span>
                </div>
                <div
                  className={`vr-view-step ${progressStep >= 2 ? "vr-view-active" : ""}`}
                >
                  <span>In Factory</span>
                </div>
                <div
                  className={`vr-view-step ${progressStep >= 3 ? "vr-view-active" : ""}`}
                >
                  <span>Finishing</span>
                </div>
                <div
                  className={`vr-view-step ${progressStep >= 4 ? "vr-view-active" : ""}`}
                >
                  <span>Ready</span>
                </div>
              </div>
            </div>
          </section>

          {/* PREMIUM AMBIENT HERO BANNER (Fixed for Mobile Compression) */}
          <section className="vr-view-hero">
            {images.length > 0 ? (
              <div
                className="vr-view-hero-img-wrap"
                onClick={() => setLightboxImg(images[0])}
              >
                {/* Blurred Ambient Background */}
                <img
                  src={images[0]}
                  alt="Background Glow"
                  className="vr-view-hero-bg-blur"
                />
                {/* Crisp Foreground Image */}
                <img
                  src={images[0]}
                  alt="Order Reference"
                  className="vr-view-hero-image"
                />
                <div className="vr-view-expand-hint">
                  <FaExpandArrowsAlt /> Tap to expand
                </div>
              </div>
            ) : (
              <div className="vr-view-hero-empty">
                <FaBoxOpen />
                <span>No Image Provided</span>
              </div>
            )}
            <div
              className={`vr-view-status-floating-badge vr-view-progress-${progressStep}`}
            >
              {status}
            </div>
          </section>

          {/* SPECS & EDITING AREA */}
          <section className="vr-view-section">
            <div className="vr-view-section-header-flex">
              <label className="vr-view-section-label">
                Order Specifications
              </label>
              {canEdit && !isEditing && (
                <button
                  className="vr-view-inline-edit-btn"
                  onClick={() => setIsEditing(true)}
                >
                  <FaEdit /> Edit Order
                </button>
              )}
            </div>

            <div className="vr-view-specs-card-grid">
              <div className="vr-view-spec-card">
                <span className="vr-view-spec-title">Type</span>
                <span className="vr-view-spec-value">{order.orderType}</span>
              </div>

              <div
                className={`vr-view-spec-card ${isEditing ? "vr-view-editing" : ""}`}
              >
                <span className="vr-view-spec-title">Est. Weight</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.weight}
                    onChange={(e) =>
                      setEditForm({ ...editForm, weight: e.target.value })
                    }
                    className="vr-view-edit-input"
                  />
                ) : (
                  <span className="vr-view-spec-value">
                    {orderDetails?.weight || "TBD"} g
                  </span>
                )}
              </div>

              <div
                className={`vr-view-spec-card ${isEditing ? "vr-view-editing" : ""}`}
              >
                <span className="vr-view-spec-title">Size/Length</span>
                {isEditing ? (
                  <input
                    type="text"
                    value={editForm.length}
                    onChange={(e) =>
                      setEditForm({ ...editForm, length: e.target.value })
                    }
                    className="vr-view-edit-input"
                  />
                ) : (
                  <span className="vr-view-spec-value">
                    {orderDetails?.length || orderDetails?.size || "TBD"}{" "}
                    {orderDetails?.lengthUnit || ""}
                  </span>
                )}
              </div>

              <div
                className={`vr-view-spec-card ${isEditing ? "vr-view-editing" : ""}`}
              >
                <span className="vr-view-spec-title">Quantity</span>
                {isEditing ? (
                  <input
                    type="number"
                    value={editForm.quantity}
                    onChange={(e) =>
                      setEditForm({ ...editForm, quantity: e.target.value })
                    }
                    className="vr-view-edit-input"
                  />
                ) : (
                  <span className="vr-view-spec-value">
                    {order.totalQuantity || orderDetails?.quantity || 1} pcs
                  </span>
                )}
              </div>

              {order.orderType === "Casting" && (
                <>
                  <div
                    className={`vr-view-spec-card ${isEditing ? "vr-view-editing" : ""}`}
                  >
                    <span className="vr-view-spec-title">Purity</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.carat}
                        onChange={(e) =>
                          setEditForm({ ...editForm, carat: e.target.value })
                        }
                        className="vr-view-edit-input"
                      />
                    ) : (
                      <span className="vr-view-spec-value">
                        {orderDetails?.carat || "TBD"}
                      </span>
                    )}
                  </div>
                  <div
                    className={`vr-view-spec-card ${isEditing ? "vr-view-editing" : ""}`}
                  >
                    <span className="vr-view-spec-title">Stone</span>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.stone}
                        onChange={(e) =>
                          setEditForm({ ...editForm, stone: e.target.value })
                        }
                        className="vr-view-edit-input"
                      />
                    ) : (
                      <span className="vr-view-spec-value">
                        {orderDetails?.stone || "None"}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </section>

          {/* INSTRUCTIONS */}
          <section className="vr-view-section">
            <label className="vr-view-section-label">
              Instructions & Notes
            </label>
            {isEditing ? (
              <textarea
                value={editForm.comments}
                onChange={(e) =>
                  setEditForm({ ...editForm, comments: e.target.value })
                }
                className="vr-view-edit-textarea"
                rows="3"
              />
            ) : (
              <div className="vr-view-instructions-box">
                <p>{order.comments || "No special instructions provided."}</p>
              </div>
            )}
          </section>

          {/* EDIT HISTORY LOG */}
          {order.editHistory && order.editHistory.length > 0 && (
            <section className="vr-view-section vr-view-edit-history-section">
              <label className="vr-view-section-label">
                <FaHistory /> Edit Log
              </label>
              <div className="vr-view-history-list">
                {order.editHistory.map((log, idx) => (
                  <div key={idx} className="vr-view-history-item">
                    <div className="vr-view-history-dot"></div>
                    <div className="vr-view-history-details">
                      <span className="vr-view-history-action">
                        {log.action} by <strong>{log.editedBy}</strong>
                      </span>
                      <span className="vr-view-history-date">
                        {log.timestamp?.seconds
                          ? new Date(
                              log.timestamp.seconds * 1000,
                            ).toLocaleString()
                          : "Recently"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div style={{ height: "100px" }}></div>
        </div>

        {/* STICKY BOTTOM BAR */}
        <footer className="vr-view-bottom-bar">
          {isEditing ? (
            <>
              <button
                className="vr-view-btn-action vr-view-cancel"
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                className="vr-view-btn-action vr-view-save"
                onClick={handleSaveChanges}
                disabled={isSaving}
              >
                {isSaving ? (
                  <FaCircleNotch className="vr-view-spinner" />
                ) : (
                  <>
                    <FaSave /> Save Changes
                  </>
                )}
              </button>
            </>
          ) : (
            <>
              <button
                className="vr-view-btn-action vr-view-support"
                onClick={() =>
                  window.open(
                    `https://wa.me/?text=Hi, regarding my Order #${order.orderId}...`,
                    "_blank",
                  )
                }
              >
                <FaWhatsapp /> Support
              </button>
              {status === "Delivered" ? (
                <button
                  className="vr-view-btn-action vr-view-primary"
                  onClick={() => navigate("/retailer/new-order")}
                >
                  Reorder
                </button>
              ) : (
                <button className="vr-view-btn-action vr-view-primary">
                  <FaMapMarkerAlt /> Track Factory
                </button>
              )}
            </>
          )}
        </footer>
      </div>
    </div>
  );
};

export default ViewRetailerOrder;
